/**
 * ProfileSwitcher component - shows current profile and allows switching.
 * Pinned to bottom of right drawer.
 */
import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Settings2, Plus, MoreHorizontal } from 'lucide-react';
import { useProfileAccess } from '@yada/ui';
import { getProfileIcon, getInitials, getProfileColor, getToolIdFromPath } from './profileDisplayUtils';
import { useProfileContext } from '../context/ProfileContext';
import { useProfiles } from './useProfiles';
import styles from './ProfileSwitcher.module.css';

interface ProfileSwitcherProps {
    onManageProfiles?: () => void;
    /**
     * If provided, called instead of switching the profile directly.
     * The parent can show a dialog and call the actual switch when appropriate.
     * If omitted, the component switches profiles immediately.
     */
    onRequestSwitch?: (toProfileId: string) => void;
}

export const ProfileSwitcher: React.FC<ProfileSwitcherProps> = ({ onManageProfiles, onRequestSwitch }) => {
    const location = useLocation();
    const { activeProfile: currentProfile, profiles, isLoading } = useProfileContext();
    const { switchProfile } = useProfiles();
    const [showAll, setShowAll] = useState(false);
    const [activatedId, setActivatedId] = useState<string | null>(null);

    const toolId = getToolIdFromPath(location.pathname);
    const context = {
        toolId,
        workspaceId: undefined, // TODO: Extract from location/state
        viewPath: location.pathname,
    };
    const accessibleProfiles = useProfileAccess(profiles, context);
    const nonAccessibleProfiles = profiles.filter(p => !accessibleProfiles.some(ap => ap.id === p.id));

    const handleSwitchProfile = async (profileId: string) => {
        if (profileId === currentProfile?.id) return; // Already on this profile
        if (onRequestSwitch) {
            // Let the parent decide (may show a dialog when a tool is active)
            onRequestSwitch(profileId);
            return;
        }
        try {
            await switchProfile(profileId);
            setActivatedId(profileId);
            setTimeout(() => setActivatedId(null), 800);
        }
        catch (err) {
            console.error('Failed to switch profile:', err);
        }
    };

    if (isLoading) {
        return (<div className={styles.root}>
        <div className={styles.loading}>Loading...</div>
      </div>);
    }
    if (!currentProfile && profiles.length === 0) {
        return (<div className={styles.root}>
        <button className={styles.createButton} onClick={onManageProfiles}>
          <Plus size={16}/>
          <span>Create Profile</span>
        </button>
      </div>);
    }
    return (<div className={styles.root}>
      <div className={styles.profileRow}>
        {/* Show accessible profiles */}
        {accessibleProfiles.map((profile, index) => {
            const Icon = getProfileIcon(profile.profileType);
            const isActive = profile.id === currentProfile?.id;
            const isHousehold = profile.profileType === 'household';
            const avatar = profile.metadata?.avatar as string | undefined;
            const initials = getInitials(profile.name);
            const color = getProfileColor(profile.id, index);
            return (<button key={profile.id} className={`${styles.profileButton} ${isActive ? styles.profileButtonActive : ''} ${activatedId === profile.id ? styles.profileButtonActivated : ''}`} onClick={() => handleSwitchProfile(profile.id)} title={profile.name} aria-label={`Switch to ${profile.name}`}>
              {avatar ? (
                <img src={avatar} alt="" className={styles.profileAvatarImg}/>
              ) : isHousehold ? (
                <Icon size={18} className={styles.profileIcon}/>
              ) : (
                <div className={styles.profileAvatar} style={{ backgroundColor: color }}>{initials}</div>
              )}
              {!isHousehold && <Icon size={10} className={styles.profileTypeIcon}/>}
      </button>);
        })}

        {/* Show non-accessible profiles if expanded */}
        {showAll && nonAccessibleProfiles.map((profile, index) => {
            const Icon = getProfileIcon(profile.profileType);
            const isActive = profile.id === currentProfile?.id;
            const isHousehold = profile.profileType === 'household';
            const avatar = profile.metadata?.avatar as string | undefined;
            const initials = getInitials(profile.name);
            const color = getProfileColor(profile.id, accessibleProfiles.length + index);
            return (<button key={profile.id} className={`${styles.profileButton} ${styles.profileButtonInaccessible} ${isActive ? styles.profileButtonActive : ''}`} onClick={() => {
                    handleSwitchProfile(profile.id);
                    setShowAll(false);
                }} title={`${profile.name} (no access to current view)`} aria-label={`Switch to ${profile.name}`}>
              {avatar ? (
                <img src={avatar} alt="" className={styles.profileAvatarImg}/>
              ) : isHousehold ? (
                <Icon size={18} className={styles.profileIcon}/>
              ) : (
                <div className={styles.profileAvatar} style={{ backgroundColor: color }}>{initials}</div>
              )}
              {!isHousehold && <Icon size={10} className={styles.profileTypeIcon}/>}
            </button>);
        })}

        {/* Collapse/expand button for non-accessible profiles */}
        {nonAccessibleProfiles.length > 0 && (<button className={styles.expandButton} onClick={() => setShowAll(!showAll)} title={`${showAll ? 'Hide' : 'Show'} ${nonAccessibleProfiles.length} other profile${nonAccessibleProfiles.length !== 1 ? 's' : ''}`} aria-label={showAll ? 'Collapse profiles' : 'Expand profiles'}>
            <MoreHorizontal size={16}/>
            {!showAll && <span className={styles.expandCount}>{nonAccessibleProfiles.length}</span>}
                </button>)}

        {/* Manage profiles button */}
            {onManageProfiles && (<button className={styles.addButton} onClick={onManageProfiles} title="Manage Profiles" aria-label="Manage Profiles">
            <Settings2 size={14}/>
                </button>)}
          </div>
    </div>);
};
